import { SalesforceIconUrl } from "@p0security/integrations-salesforce-shared/icon";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { DEFAULT_WIF_REQUEST_PARAMETER } from "@p0security/shared/types/agentic/credential";
import type { McpServerSpec } from "@p0security/shared/types/agentic/mcp";

import { salesforceAiMcpTools } from "./mcp-tools.ts";
import type { SalesforceAiIntegration } from "./types.ts";

const prefixArgs = ["salesforce-ai", "permission-set"];

/** Salesforce's hosted MCP server, called as an API-only agent user that P0
 * provisions per gateway session. The agent's permission sets decide what
 * each tool can reach. */
export const salesforceAiMcp: McpServerSpec & { type: "external" } = {
  type: "external",
  label: "Salesforce",
  iconUrl: SalesforceIconUrl,
  // The hosted server finds the org from the token.
  url: "https://api.salesforce.com/platform/mcp/v1/platform/sobject-all",
  credential: {
    type: "wif",
    csp: "generic",
    parameter: DEFAULT_WIF_REQUEST_PARAMETER,
    // The grant names the org: one server serves every installed org.
    tokenEndpoint: "{{ request.generated.tokenExchange.tokenEndpoint }}",
    requestParams: {
      client_id: "{{ request.generated.tokenExchange.clientId }}",
      // The hosted server needs `mcp_api`; without `api` the token endpoint
      // answers 404.
      scope: "api mcp_api",
    },
    // The P0 package's token-exchange handler checks `aud`, and finds the
    // user from the token's `sub`.
    claims: '{ "aud": {{ request.generated.tokenExchange.audience | json }} }',
  },
  authorization: "upstream",
  tools: { type: "manual", tools: salesforceAiMcpTools },
  access: {
    prompt: `Request Salesforce access by calling with argv:
  [...<permission-sets>, "--reason", <reason>]
where arguments are:
  - permission-sets: API name(s) of the Salesforce permission sets your tool calls need;
    use the "list" tool to see the ones you can request
  - reason: Business justification for the requested access
You act as your own Salesforce user, which has only the permission sets you are granted.
`,
    prefixArgs,
  },
  list: {
    fields: {
      permissionSet: {
        description: "Salesforce permission set, by API name",
        required: [],
      },
    },
    prefixArgs,
  },
};

/** Whether {@link salesforceAiMcp} can be added: only once an org is
 * installed, since only that install provisions agent users. */
export const salesforceAiMcpServerAvailability = {
  salesforce: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "org",
      (installed["salesforce-ai"] ?? {}) as SalesforceAiIntegration
    ).length > 0,
};
